/**
 * LocaleRequiredRouter: Handle routing to a page based on the locale. Does not
 * allow any locales by default, you should add "en" if you want it to display
 * for "en" locales.
 *
 * If a non-matching locale attempts to access the page then the user will
 * see an error message stating that the page can't be found.
 */
import * as React from "react";
import {Route} from "react-router-dom";

import {RequestInfo} from "@khan/shared-core";

import AsyncRouteHandler from "./async-route-handler.tsx";
import RouteLoadSpinner from "./route-load-spinner.tsx";
import type {CommonRouteProps} from "./types.ts";

type DefaultProps = {
    // The locales to allow access to
    // Defaults to an empty array
    locales: Array<string>;
};
type Props = DefaultProps & CommonRouteProps;

const NotFound = React.lazy(() => import("./errors/not-found.tsx"));

export default class LocaleRequiredRoute extends React.Component<Props> {
    static defaultProps: DefaultProps = {
        locales: [],
    };

    isAllowedToView(): boolean {
        return this.props.locales.includes(RequestInfo.kaLocale);
    }

    render(): React.ReactNode {
        const {exact, path, component} = this.props;
        return (
            <Route
                path={path}
                exact={exact}
                render={() => {
                    if (this.isAllowedToView()) {
                        return (
                            <AsyncRouteHandler
                                component={component}
                                template={this.props.template}
                            />
                        );
                    }
                    // If the current locale doesn't match then a NotFound
                    // error is returned to the user.
                    return (
                        <React.Suspense fallback={<RouteLoadSpinner />}>
                            <NotFound />
                        </React.Suspense>
                    );
                }}
            />
        );
    }
}
